import React from "react";
import { MdInfoOutline, MdMonitorHeart } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import { IconButton } from "components/button";
import Tooltip from "components/tooltip";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState } from "components/states";
import CardError from "./CardError";

const SCORE_TONES = {
  low: { stroke: "stroke-danger", fill: "bg-danger", text: "text-danger-fg" },
  fair: { stroke: "stroke-warning", fill: "bg-warning", text: "text-warning-fg" },
  good: { stroke: "stroke-success", fill: "bg-success", text: "text-success-fg" },
};

const getScoreTone = (score) => {
  if (score <= 40) return SCORE_TONES.low;
  if (score <= 70) return SCORE_TONES.fair;
  return SCORE_TONES.good;
};

const breakdownLabels = {
  spiritualGrowth: "Spiritual Growth",
  attendance: "Attendance",
  engagement: "Engagement",
  communityHealth: "Community Health",
};

const METHODOLOGY = [
  { label: "Spiritual Growth", desc: "Heartbeat survey scores and salvation trends" },
  { label: "Attendance", desc: "Service attendance trends over the last 3 months" },
  { label: "Engagement", desc: "Staff & volunteer pulse response rates and scores" },
  { label: "Community Health", desc: "People growth, guest retention, and volunteer participation" },
];

const GAUGE_RADIUS = 45;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

const CircularGauge = ({ score }) => {
  const progress = (score / 100) * GAUGE_CIRCUMFERENCE;
  const tone = getScoreTone(score);

  return (
    <div className="relative flex items-center justify-center">
      <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
        <circle
          cx="60"
          cy="60"
          r={GAUGE_RADIUS}
          fill="none"
          strokeWidth="8"
          className="stroke-surface-subtle"
        />
        <circle
          cx="60"
          cy="60"
          r={GAUGE_RADIUS}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={GAUGE_CIRCUMFERENCE}
          strokeDashoffset={GAUGE_CIRCUMFERENCE - progress}
          transform="rotate(-90 60 60)"
          className={`${tone.stroke} transition-all duration-1000`}
        />
      </svg>
      <span className={`absolute text-4xl font-bold ${tone.text}`}>
        {score}
        <span className="sr-only"> out of 100</span>
      </span>
    </div>
  );
};

const BreakdownBar = ({ label, value }) => {
  const tone = getScoreTone(value);
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs font-medium text-fg-muted">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-subtle">
        <div
          className={`h-full rounded-full transition-all duration-700 ${tone.fill}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="w-8 text-right text-xs font-semibold text-fg">{value}</span>
    </div>
  );
};

const methodology = (
  <div className="flex flex-col gap-2 py-1">
    <p>
      An AI-generated composite score (0 to 100) based on key indicators from your organization.
    </p>
    <ul className="flex flex-col gap-1.5">
      {METHODOLOGY.map((item) => (
        <li key={item.label}>
          <span className="font-semibold">{item.label}:</span> {item.desc}
        </li>
      ))}
    </ul>
    <p className="opacity-80">Refreshes every 24 hours. Higher is better.</p>
  </div>
);

const ChurchHealthScore = ({ data, loading, error, onRetry }) => {
  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center gap-4" aria-busy="true">
          <Skeleton className="h-28 w-28 rounded-full" />
          <Skeleton lines={4} className="w-full" />
        </div>
      );
    }
    if (error) {
      return <CardError title="Couldn't Load Health Score" error={error} onRetry={onRetry} />;
    }
    if (!data) {
      return (
        <EmptyState
          variant="inline"
          icon={MdMonitorHeart}
          title="Unable to generate a health score yet"
        />
      );
    }
    return (
      <>
        <div className="flex flex-col items-center">
          <CircularGauge score={data.overall || 0} />
        </div>
        {data.breakdown && (
          <div className="flex flex-col gap-2.5">
            {Object.entries(breakdownLabels).map(([key, label]) => (
              <BreakdownBar key={key} label={label} value={data.breakdown[key] || 0} />
            ))}
          </div>
        )}
        {data.summary && <p className="text-xs leading-relaxed text-fg-muted">{data.summary}</p>}
      </>
    );
  };

  return (
    <Card padding="md" className="gap-4">
      <CardHeader
        title="Church Health Score"
        actions={
          <Tooltip
            placement="bottom-end"
            content={methodology}
            trigger={
              <IconButton
                size="sm"
                icon={<MdInfoOutline aria-hidden="true" className="h-5 w-5" />}
                label="How is this calculated?"
                title={undefined}
              />
            }
          />
        }
      />
      {renderBody()}
    </Card>
  );
};

export default ChurchHealthScore;
